import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    QrCode, Power, Play, Paperclip, Smile, Mic, ChevronRight,
    User as UserIcon, Settings, MessageSquare, HardDrive, Cable, Activity, Mail, Phone,
    Send, FileText, Table as TableIcon, BarChart3, Download, ExternalLink,
    Award, GraduationCap, Star, Languages, Clock, MapPin, Plus, X, Sparkles,
    CheckCircle2, MoreHorizontal, AlertTriangle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import api, { API_BASE, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import IntegrationIcon from "@/components/IntegrationIcon";
import ConnectorOAuthModal from "@/components/ConnectorOAuthModal";
import IntegrationPickerModal from "@/components/IntegrationPickerModal";
import TraitDots, { TraitCards } from "@/components/TraitDots";
import CustomSkillsPanel from "@/components/CustomSkillsPanel";

/* ---------- Small helpers ---------- */
const fmtTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
};
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const STATUS_COLOR = {
    done: "bg-[#16a34a]",
    running: "bg-blue-500",
    paused: "bg-amber-500",
    failed: "bg-red-500",
};

/* ---------- Terminal box (right rail bottom) ---------- */
function TerminalBox({ workerName, company }) {
    const [awake, setAwake] = useState(false);
    const host = (workerName || "worker").toLowerCase();
    const org = (company?.slug || "acme").toLowerCase();
    return (
        <div
            className="rounded-xl border border-[#27272a] bg-[#0a0a0a] text-[#a7f3d0] font-mono text-[11px] leading-relaxed overflow-hidden"
            data-testid="terminal-box"
        >
            <div className="px-3 py-2 border-b border-[#27272a] flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-gray-500">Computer</span>
                <span className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#f87171]" />
                    <span className="w-2 h-2 rounded-full bg-[#fbbf24]" />
                    <span className="w-2 h-2 rounded-full bg-[#22c55e]" />
                </span>
            </div>
            <div className="p-3 space-y-1">
                {awake ? (
                    <>
                        <div className="text-white/60">{host}@{org}:~$ <span className="text-white">wake</span></div>
                        <div>[ ok ] display powered on</div>
                        <div>[ ok ] loading session state…</div>
                        <div>[ ok ] tasks queue: 3 running, 0 failed</div>
                        <div className="text-white/60">{host}@{org}:~$ <span className="wfos-caret" /></div>
                    </>
                ) : (
                    <>
                        <div className="text-white/60">{host}@{org}:~$ <span className="text-white">suspend</span></div>
                        <div>[ ok ] session state saved</div>
                        <div>[ ok ] display powered off</div>
                        <div className="opacity-60">zZ</div>
                    </>
                )}
            </div>
            <div className="p-3 pt-0">
                <button
                    onClick={() => setAwake((v) => !v)}
                    data-testid="terminal-wake-desktop"
                    className="w-full rounded-md border border-[#27272a] bg-[#0a0a0a] hover:bg-[#141416] py-1.5 text-[11px] text-[#a7f3d0]"
                >
                    {awake ? "Suspend" : "Wake desktop"}
                </button>
            </div>
        </div>
    );
}

/* ---------- Mini bar chart (M T W T F S S) ---------- */
function MiniBar({ values }) {
    const max = Math.max(1, ...values);
    return (
        <div className="flex items-end gap-0.5 h-6">
            {values.map((v, i) => (
                <div
                    key={i}
                    style={{ height: `${(v / max) * 100}%` }}
                    className="w-1 rounded-t-sm bg-[#16a34a]/70"
                />
            ))}
        </div>
    );
}

/* ---------- Artifact card ---------- */
function ArtifactCard({ artifact }) {
    const iconMap = { table: TableIcon, document: FileText, chart: BarChart3 };
    const Icon = iconMap[artifact.type] || FileText;
    return (
        <div className="mt-2 wfos-card p-3 flex items-center gap-3 max-w-sm">
            <div className="w-9 h-9 rounded-lg bg-[#dcfce7] grid place-items-center flex-shrink-0">
                <Icon className="w-4.5 h-4.5 text-[#16a34a]" strokeWidth={1.75} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-gray-900 truncate">{artifact.title}</div>
                <div className="text-[11px] text-gray-500 truncate">{artifact.subtitle}</div>
            </div>
            <div className="flex gap-1">
                <button className="p-1.5 rounded-md hover:bg-[#f4f4ef]" title="Open">
                    <ExternalLink className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
                </button>
                <button className="p-1.5 rounded-md hover:bg-[#f4f4ef]" title="Download">
                    <Download className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
                </button>
            </div>
        </div>
    );
}

/* ---------- Professional Identity block (left rail) ---------- */
function IdentityBlock({ worker }) {
    if (!worker) return null;
    const years = worker.years_experience;
    const spec = worker.specialization;
    const bio = worker.bio;
    const creds = worker.credentials || [];
    const highlights = worker.career_highlights || [];
    const langs = worker.languages_spoken || [];
    const tz = worker.timezone_preference;
    const availability = worker.availability;
    const sig = worker.signature_style;

    return (
        <div className="mt-6 space-y-4" data-testid="worker-identity-block">
            {(years || spec) && (
                <div className="wfos-card p-3">
                    <div className="flex flex-wrap gap-1.5 items-center">
                        {years !== undefined && years !== null && (
                            <span className="wfos-pill wfos-pill--green" data-testid="identity-experience">
                                <Star className="w-3 h-3" strokeWidth={2} /> {years} yrs experience
                            </span>
                        )}
                        {availability && (
                            <span className="wfos-pill" data-testid="identity-availability">
                                <Clock className="w-3 h-3" strokeWidth={2} /> {availability}
                            </span>
                        )}
                    </div>
                    {spec && (
                        <div className="mt-2 text-[12px] text-gray-700 leading-relaxed" data-testid="identity-specialization">
                            <span className="wfos-label">Specialization</span>
                            <div className="mt-0.5">{spec}</div>
                        </div>
                    )}
                </div>
            )}

            {bio && (
                <div>
                    <div className="wfos-label mb-1.5">About</div>
                    <p className="text-[12.5px] text-gray-700 leading-relaxed" data-testid="identity-bio">
                        {bio}
                    </p>
                </div>
            )}

            {creds.length > 0 && (
                <div>
                    <div className="wfos-label mb-1.5 flex items-center gap-1.5">
                        <GraduationCap className="w-3 h-3 text-[#16a34a]" strokeWidth={2} /> Credentials
                    </div>
                    <ul className="space-y-1.5 text-[12px] text-gray-700" data-testid="identity-credentials">
                        {creds.map((c, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#16a34a] flex-shrink-0" />
                                <span className="leading-relaxed">{c}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {highlights.length > 0 && (
                <div>
                    <div className="wfos-label mb-1.5 flex items-center gap-1.5">
                        <Award className="w-3 h-3 text-[#16a34a]" strokeWidth={2} /> Career highlights
                    </div>
                    <ul className="space-y-1.5 text-[12px] text-gray-700" data-testid="identity-highlights">
                        {highlights.map((h, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#16a34a] flex-shrink-0" />
                                <span className="leading-relaxed">{h}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {langs.length > 0 && (
                <div>
                    <div className="wfos-label mb-1.5 flex items-center gap-1.5">
                        <Languages className="w-3 h-3 text-[#16a34a]" strokeWidth={2} /> Languages
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="identity-languages">
                        {langs.map((l, i) => (
                            <span key={i} className="wfos-pill text-[10px]">
                                {typeof l === "string" ? l : `${l.lang} · ${l.level}`}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Sprint D Addendum #2 — Character sub-block */}
            {(worker.character_traits || []).length > 0 && (
                <div data-testid="identity-character">
                    <div className="wfos-label mb-1.5">Character</div>
                    <TraitCards traits={worker.character_traits} />
                    <div className="mt-2 text-[11px] italic text-gray-500 leading-snug">
                        "Not a personality template. A real character."
                    </div>
                </div>
            )}

            {tz && (
                <div className="text-[11.5px] text-gray-500 flex items-center gap-1.5" data-testid="identity-timezone">
                    <MapPin className="w-3 h-3 text-gray-400" strokeWidth={2} /> {tz.replace("_", " ")}
                </div>
            )}

            {sig && (
                <div className="wfos-card p-3 bg-[#fafaf7]" data-testid="identity-signature">
                    <div className="wfos-label mb-1">Signature</div>
                    <div className="text-[12px] italic text-gray-600 leading-relaxed">{sig}</div>
                </div>
            )}
        </div>
    );
}

/* ---------- Connectors panel (left rail Configure section) ---------- */
function ConnectorsPanel({ integrations, onAdd, onDisconnect }) {
    const connected = integrations?.connected || [];
    return (
        <div className="mt-6" data-testid="connectors-panel">
            <div className="flex items-center justify-between mb-2">
                <div className="wfos-label flex items-center gap-1.5">
                    <Cable className="w-3 h-3 text-[#16a34a]" strokeWidth={2} /> Connectors
                </div>
                <button
                    onClick={onAdd}
                    data-testid="connectors-add-btn"
                    className="inline-flex items-center gap-0.5 rounded-md bg-white border border-[#e8e8e3] hover:border-[#d6d6cf] px-2 h-6 text-[11px] text-gray-700"
                >
                    <Plus className="w-3 h-3" strokeWidth={2} /> Add
                </button>
            </div>
            {connected.length === 0 ? (
                <div
                    data-testid="connectors-empty"
                    className="wfos-card p-3 text-[12px] text-gray-500 leading-relaxed"
                >
                    No tools connected yet.
                    <button
                        onClick={onAdd}
                        data-testid="connectors-empty-cta"
                        className="ml-1 text-[#16a34a] font-medium hover:underline"
                    >
                        Connect the first tool
                    </button>
                </div>
            ) : (
                <ul className="space-y-1">
                    {connected.map((c) => (
                        <li
                            key={c.connection_id || c.id}
                            data-testid={`connector-row-${c.connection_id || c.id}`}
                            className="wfos-card p-2 grid grid-cols-[auto_1fr_auto] items-center gap-2"
                        >
                            {/* Icon with green-check badge overlay */}
                            <div className="relative flex-shrink-0">
                                <IntegrationIcon integration={c} size="sm" />
                                <span
                                    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white grid place-items-center shadow-sm"
                                    data-testid={`connector-check-${c.connection_id || c.id}`}
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2.5} fill="white" />
                                </span>
                            </div>

                            {/* Name + email-format label */}
                            <div className="min-w-0">
                                <div className="text-[12.5px] font-medium text-gray-900 truncate">{c.name}</div>
                                <div
                                    className="text-[10px] text-gray-500 truncate"
                                    data-testid={`connector-label-${c.connection_id || c.id}`}
                                    title={c.account_label}
                                >
                                    Connected as {c.account_label || "—"}
                                </div>
                            </div>

                            {/* Manage button — always in the same column position */}
                            <button
                                onClick={() => onDisconnect?.(c)}
                                title="Manage connection"
                                data-testid={`connector-manage-${c.connection_id || c.id}`}
                                className="justify-self-end inline-flex items-center gap-0.5 rounded-md border border-[#e8e8e3] bg-white hover:border-[#d6d6cf] px-2 h-6 text-[10.5px] font-medium text-gray-700"
                            >
                                <MoreHorizontal className="w-3 h-3" strokeWidth={2} /> Manage
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <Link
                to="/integrations"
                target="_blank"
                data-testid="connectors-browse-all"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#16a34a] hover:text-[#15803d]"
            >
                Browse all 600+ integrations
                <ExternalLink className="w-3 h-3" strokeWidth={2} />
            </Link>
        </div>
    );
}

/* ---------- "Lives in" strip (center column top) ---------- */
function LivesInStrip({ integrations, onAdd }) {
    const connected = integrations?.connected || [];
    return (
        <div
            className="border-b border-[#e8e8e3] bg-[#fafaf7]/60 px-6 py-3 flex items-center gap-3 flex-wrap"
            data-testid="lives-in-strip"
        >
            <span className="wfos-label whitespace-nowrap">Lives in</span>
            {connected.length === 0 ? (
                <span className="text-[12.5px] text-gray-500 flex items-center gap-2 flex-wrap">
                    No tools connected yet —
                    <button
                        onClick={onAdd}
                        data-testid="lives-in-empty-cta"
                        className="text-[#16a34a] font-medium hover:underline inline-flex items-center gap-1"
                    >
                        Connect the first tool <ExternalLink className="w-3 h-3" strokeWidth={2} />
                    </button>
                </span>
            ) : (
                <>
                    <div className="flex -space-x-1.5" data-testid="lives-in-icons">
                        {connected.slice(0, 8).map((c) => (
                            <IntegrationIcon key={c.id} integration={c} size="sm" withRing testid={`lives-in-icon-${c.id}`} />
                        ))}
                    </div>
                    <span className="text-[11.5px] text-gray-500 truncate">
                        {connected.slice(0, 4).map((c) => c.name).join(" · ")}
                        {connected.length > 4 ? ` +${connected.length - 4} more` : ""}
                    </span>
                    <button
                        onClick={onAdd}
                        data-testid="lives-in-add"
                        className="ml-auto inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] bg-white hover:border-[#d6d6cf] px-2 h-7 text-[11px] text-gray-700"
                    >
                        <Plus className="w-3 h-3" strokeWidth={2} /> Add
                    </button>
                </>
            )}
        </div>
    );
}

/* ==========================================================================
   Main WorkerPage
   ========================================================================== */
export default function WorkerPage() {
    const { hiredId } = useParams();
    const { company } = useAuth();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [streamText, setStreamText] = useState("");
    const [error, setError] = useState(null);
    const [capNotice, setCapNotice] = useState(null); // Sprint G G-5 — 429 banner
    const feedRef = useRef(null);

    /* Integrations state */
    const [integrations, setIntegrations] = useState({ connected: [], recommended: [] });
    const [catalog, setCatalog] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pendingOAuth, setPendingOAuth] = useState(null); // integration to authorize
    const [oauthBusy, setOauthBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/workers/hired/${hiredId}`);
            setDetail(data);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load worker");
        } finally {
            setLoading(false);
        }
    };

    const loadIntegrations = async () => {
        try {
            const { data } = await api.get(`/workers/hired/${hiredId}/integrations`);
            setIntegrations(data);
        } catch (e) {
            // Surface — don't silently misrepresent a backend outage as empty state.
            const msg = e?.response?.data?.detail || e?.message || "Failed to load integrations";
            toast.error(`Integrations: ${msg}`);
        }
    };

    const loadCatalog = async () => {
        if (catalog) return;
        try {
            const { data } = await api.get("/integrations/catalog");
            setCatalog(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Failed to load integrations catalog";
            toast.error(`Catalog: ${msg}`);
        }
    };

    useEffect(() => { load(); loadIntegrations(); loadCatalog(); /* eslint-disable-next-line */ }, [hiredId]);

    useEffect(() => {
        if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }, [detail?.conversation?.messages?.length, streamText, streaming]);

    const worker = detail?.worker;
    const hired = detail?.hired_worker;
    const messages = detail?.conversation?.messages || [];
    const activity = detail?.activity || [];

    const stats = useMemo(() => {
        const done = activity.filter((a) => a.status === "done").length;
        const running = activity.filter((a) => a.status === "running").length;
        const paused = activity.filter((a) => a.status === "paused").length;
        const failed = activity.filter((a) => a.status === "failed").length;
        const now = new Date();
        const buckets = Array(7).fill(0);
        activity.forEach((a) => {
            const d = new Date(a.timestamp);
            const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
            if (days >= 0 && days < 7) buckets[6 - days] += 1;
        });
        return { done, running, paused, failed, bars: buckets };
    }, [activity]);

    const openPicker = () => { loadCatalog(); setPickerOpen(true); };
    const closePicker = () => setPickerOpen(false);

    const startOAuth = (integration) => {
        setPickerOpen(false);
        setPendingOAuth(integration);
    };
    const cancelOAuth = () => { setPendingOAuth(null); setOauthBusy(false); };

    const confirmAuthorize = async (scopes) => {
        if (!pendingOAuth) return;
        setOauthBusy(true);
        try {
            await api.post(`/workers/hired/${hiredId}/integrations/connect`, {
                integration_id: pendingOAuth.id,
                scopes,
            });
            toast.success(`${pendingOAuth.name} connected`);
            setPendingOAuth(null);
            await loadIntegrations();
            // refresh activity feed so the new activity row appears
            const r = await api.get(`/workers/hired/${hiredId}/activity`);
            setDetail((prev) => ({ ...prev, activity: r.data.activity }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Connect failed");
        } finally {
            setOauthBusy(false);
        }
    };

    const disconnect = async (integ) => {
        if (!window.confirm(`Disconnect ${integ.name}?`)) return;
        try {
            await api.post(`/workers/hired/${hiredId}/integrations/${integ.id}/disconnect`);
            toast.success(`${integ.name} disconnected`);
            await loadIntegrations();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Disconnect failed");
        }
    };

    const sendMessage = async (e) => {
        e?.preventDefault();
        if (!input.trim() || streaming) return;
        if (!worker?.is_functional) {
            toast.error("This Workforce Member isn't activated yet.");
            return;
        }
        const text = input;
        setInput("");
        setStreaming(true);
        setStreamText("");

        const now = new Date().toISOString();
        setDetail((prev) => ({
            ...prev,
            conversation: {
                ...(prev.conversation || {}),
                messages: [
                    ...(prev.conversation?.messages || []),
                    { role: "user", content: text, timestamp: now },
                ],
            },
        }));

        const token = localStorage.getItem("wfos_token");
        const requestId = (crypto?.randomUUID?.() || `req-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        try {
            const res = await fetch(`${API_BASE}/workers/hired/${hiredId}/message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Request-ID":  requestId,
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ content: text }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                // Sprint G G-5: tenant cap-reached response
                if (res.status === 429) {
                    const d = err?.detail || {};
                    const msg = d.reason || "You've reached today's usage cap.";
                    setCapNotice(msg);
                    toast.error(msg);
                    setStreaming(false);
                    setStreamText("");
                    return;
                }
                // Sprint J-3 · fetch-path 402 paywalls open PaywallModal.
                if (res.status === 402) {
                    const detail = err?.detail ?? err;
                    const paywall = typeof detail === "object" && detail !== null ? detail : { message: String(detail) };
                    paywall._request_url = `/workers/hired/${hiredId}/message`;
                    window.dispatchEvent(new CustomEvent("wfos:paywall", { detail: paywall }));
                    setStreaming(false);
                    setStreamText("");
                    return;
                }
                throw new Error(err?.detail?.message || err?.detail || `HTTP ${res.status}`);
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let final = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const chunks = buffer.split("\n\n");
                buffer = chunks.pop();
                for (const c of chunks) {
                    const line = c.trim();
                    if (!line.startsWith("data:")) continue;
                    const payloadStr = line.slice(5).trim();
                    if (!payloadStr) continue;
                    let payload;
                    try { payload = JSON.parse(payloadStr); } catch { continue; }
                    if (payload.type === "delta") { final += payload.content; setStreamText(final); }
                    else if (payload.type === "done") { final = payload.content || final; }
                    else if (payload.type === "error") { throw new Error(payload.message); }
                }
            }
            setDetail((prev) => ({
                ...prev,
                conversation: {
                    ...(prev.conversation || {}),
                    messages: [
                        ...(prev.conversation?.messages || []),
                        { role: "assistant", content: final, timestamp: new Date().toISOString() },
                    ],
                },
            }));
            api.get(`/workers/hired/${hiredId}/activity`).then((r) => {
                setDetail((prev) => ({ ...prev, activity: r.data.activity }));
            });
        } catch (err) {
            toast.error(String(err.message || err));
        } finally {
            setStreaming(false);
            setStreamText("");
        }
    };

    if (loading) {
        return <div className="h-full grid place-items-center text-sm text-gray-500">Loading worker…</div>;
    }
    if (error) {
        return (
            <div className="h-full grid place-items-center text-sm text-gray-500">
                {error} · <Link to="/app/workforce" className="text-[#16a34a] ml-2">Back</Link>
            </div>
        );
    }

    const displayEmail = (worker.contact_email_template || `${worker.slug}@workforce.ai`)
        .replace("{company_slug}", company?.slug || "acme");
    const displayPhone = worker.contact_phone_template || "+1 (415) 555-0100";
    const connectedIds = new Set((integrations.connected || []).map((c) => c.id));

    return (
        <div className="h-full flex flex-col xl:flex-row overflow-y-auto xl:overflow-hidden" data-testid="worker-page-root">
            {/* LEFT RAIL — identity card */}
            <aside
                className="w-full xl:w-[300px] flex-shrink-0 border-b xl:border-b-0 xl:border-r border-[#e8e8e3] bg-[#fafaf7] flex flex-col xl:overflow-y-auto wfos-scrollbar"
                data-testid="worker-left-rail"
            >
                <div className="p-6 flex flex-col">
                    <img
                        src={resolveAvatar(worker.avatar_url)}
                        alt={worker.name}
                        data-testid="worker-avatar"
                        className="w-full aspect-[3/4] rounded-xl object-cover border border-[#e8e8e3]"
                    />
                    <div className="mt-4">
                        <div className="text-lg font-semibold text-gray-900" data-testid="worker-name">
                            {hired.custom_name || worker.name}
                        </div>
                        <div
                            className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-widest uppercase text-gray-600"
                            data-testid="worker-role-tag"
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${worker.is_functional ? "bg-[#16a34a]" : "bg-gray-400"}`} />
                            {worker.role} Workforce Member
                        </div>
                        {(worker.character_traits || []).length > 0 && (
                            <TraitDots
                                traits={worker.character_traits}
                                size="sm"
                                className="mt-2"
                            />
                        )}
                    </div>

                    <div className="mt-4 flex items-center gap-2">
                        <button
                            data-testid="worker-start-session"
                            disabled={!worker.is_functional}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#111827] px-3 h-8 text-xs font-medium text-white hover:bg-black disabled:bg-gray-200 disabled:text-gray-400"
                        >
                            <Play className="w-3 h-3" strokeWidth={2} /> Start live session
                        </button>
                        <button
                            data-testid="worker-share-qr"
                            className="w-8 h-8 rounded-full border border-[#e8e8e3] bg-white grid place-items-center text-gray-500 hover:text-gray-900 hover:border-[#d6d6cf]"
                            title="Share"
                        >
                            <QrCode className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                        <button
                            data-testid="worker-power-toggle"
                            className="w-8 h-8 rounded-full border border-[#e8e8e3] bg-white grid place-items-center text-gray-500 hover:text-gray-900 hover:border-[#d6d6cf]"
                            title="Pause"
                        >
                            <Power className="w-3.5 h-3.5" strokeWidth={2} />
                        </button>
                    </div>

                    <div className="mt-5 space-y-1.5 text-[13px]">
                        <div className="flex items-center gap-2 text-gray-700" data-testid="worker-email">
                            <Mail className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                            <span className="truncate">{displayEmail}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-700" data-testid="worker-phone">
                            <Phone className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                            <span>{displayPhone}</span>
                        </div>
                    </div>

                    {/* Professional Identity */}
                    <IdentityBlock worker={worker} />

                    <div className="mt-6">
                        <div className="wfos-label mb-2">Activity</div>
                        <button
                            className="w-full text-left inline-flex items-center justify-between rounded-lg bg-white border border-[#e8e8e3] px-3 h-9 text-sm text-gray-900"
                            data-testid="worker-activity-tab"
                        >
                            <span className="inline-flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} /> Live activity</span>
                            <ChevronRight className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
                        </button>
                    </div>

                    {/* Connectors (live) */}
                    <ConnectorsPanel
                        integrations={integrations}
                        onAdd={openPicker}
                        onDisconnect={disconnect}
                    />

                    <div className="mt-6">
                        <div className="wfos-label mb-2">Configure</div>
                        <ul className="space-y-1 text-[13px] text-gray-600">
                            {[
                                { key: "profile",       label: "Profile",       Icon: UserIcon },
                                { key: "agent",         label: "Agent",         Icon: Settings },
                                { key: "communication", label: "Communication", Icon: MessageSquare },
                                { key: "drive",         label: "Drive",         Icon: HardDrive },
                                { key: "monitoring",    label: "Monitoring",    Icon: Activity },
                            ].map(({ key, label, Icon }) => (
                                <li key={key}>
                                    <button
                                        data-testid={`worker-configure-${key}`}
                                        className="w-full inline-flex items-center gap-2 rounded-md px-2 h-8 text-left hover:bg-[#f4f4ef]"
                                    >
                                        <Icon className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                                        <span>{label}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Phase 5-A — Custom Skills */}
                    <CustomSkillsPanel
                        hiredId={hiredId}
                        workerName={worker?.name}
                        workerRole={worker?.role}
                    />
                </div>
            </aside>

            {/* CENTER */}
            <main className="flex-1 flex flex-col bg-white min-w-0 min-h-[60vh] xl:min-h-0" data-testid="worker-main-column">
                {/* Sprint I-6 — Specialty disclaimer chip + modal */}
                <SpecialtyDisclaimerBar workerSlug={worker.slug} disclaimerKey={worker.specialty_disclaimer_key} />

                {/* Center-column character trait dots — subtle reinforcement of the
                    persona. No labels here, tooltip only. Left rail already carries
                    the labeled version. */}
                {(worker.character_traits || []).length > 0 && (
                    <div
                        data-testid="worker-center-traits"
                        className="border-b border-[#e8e8e3] px-6 py-2.5 flex items-center gap-2"
                    >
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                            {worker.name}
                        </span>
                        <span className="text-gray-300">·</span>
                        <TraitDots traits={worker.character_traits} size="md" />
                    </div>
                )}

                {/* Non-functional banner */}
                {!worker.is_functional && (
                    <div className="border-b border-[#e8e8e3] bg-[#fef9c3]/50 px-6 py-3 text-[13px] text-amber-900" data-testid="worker-nonfunctional-banner">
                        This Workforce Member is available but hasn't been activated. Coming soon.
                    </div>
                )}

                {/* Lives-in strip */}
                <LivesInStrip integrations={integrations} onAdd={openPicker} />

                {/* Stat strip */}
                <div className="grid grid-cols-4 gap-3 p-6 border-b border-[#e8e8e3]">
                    <div className="wfos-card p-3" data-testid="stat-tasks-completed">
                        <div className="wfos-label">Tasks completed</div>
                        <div className="mt-1 text-xl font-semibold text-gray-900">{stats.done}</div>
                    </div>
                    <div className="wfos-card p-3" data-testid="stat-avg-runtime">
                        <div className="wfos-label">Avg runtime</div>
                        <div className="mt-1 text-xl font-semibold text-gray-900">1.4s</div>
                    </div>
                    <div className="wfos-card p-3" data-testid="stat-credits">
                        <div className="wfos-label">Credits</div>
                        <div className="mt-1 text-xl font-semibold text-gray-900">8,240</div>
                    </div>
                    <div className="wfos-card p-3" data-testid="stat-this-week">
                        <div className="wfos-label">This week</div>
                        <div className="mt-1 flex items-end justify-between gap-2">
                            <MiniBar values={stats.bars} />
                            <div className="text-[11px] text-gray-500 font-mono">M T W T F S S</div>
                        </div>
                    </div>
                </div>

                {/* Conversation feed */}
                <div ref={feedRef} className="flex-1 overflow-y-auto wfos-scrollbar px-6 py-6 space-y-6" data-testid="conversation-feed">
                    {messages.length === 0 && !streaming && (
                        <div className="max-w-lg mx-auto text-center py-10">
                            <div className="w-14 h-14 mx-auto rounded-full bg-[#dcfce7] grid place-items-center">
                                <MessageSquare className="w-6 h-6 text-[#16a34a]" strokeWidth={1.75} />
                            </div>
                            <div className="mt-4 text-lg font-medium text-gray-900">Say hi to {worker.name}.</div>
                            <div className="mt-1 text-sm text-gray-500">{worker.description}</div>
                        </div>
                    )}

                    {messages.map((m, i) => (
                        <MessageRow key={i} m={m} worker={worker} />
                    ))}

                    {streaming && (
                        <MessageRow
                            m={{ role: "assistant", content: streamText, timestamp: new Date().toISOString() }}
                            worker={worker}
                            streaming
                        />
                    )}
                </div>

                {/* Composer */}
                <form onSubmit={sendMessage} className="border-t border-[#e8e8e3] p-4 bg-white">
                    {capNotice && (
                        <div
                            className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2"
                            data-testid="tenant-cap-banner"
                        >
                            <Sparkles className="w-4 h-4 text-amber-700" strokeWidth={2} />
                            <div className="text-[12.5px] text-amber-900 leading-relaxed flex-1">
                                <b>Usage cap reached.</b> {capNotice}
                            </div>
                            <Link
                                to="/app/billing"
                                data-testid="tenant-cap-upgrade"
                                className="rounded-md bg-amber-800 text-white px-2.5 h-7 inline-flex items-center text-[11px] font-semibold hover:bg-amber-900"
                            >
                                Upgrade
                            </Link>
                            <button
                                type="button"
                                onClick={() => setCapNotice(null)}
                                className="text-amber-800 hover:text-amber-950"
                                title="Dismiss"
                            >
                                <X className="w-4 h-4" strokeWidth={2} />
                            </button>
                        </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                        <span className="ml-auto wfos-pill" data-testid="composer-hint">
                            <Sparkles className="w-3 h-3 text-[#16a34a]" strokeWidth={2} />
                            {worker.is_functional ? "Streaming via Claude Sonnet 4.5" : "Finish setting up your Workforce Member"}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 rounded-2xl border border-[#e8e8e3] bg-[#fafaf7] px-3 py-2 focus-within:border-[#16a34a] focus-within:ring-2 focus-within:ring-[#16a34a]/20">
                        <button type="button" className="p-1.5 rounded-md text-gray-500 hover:text-gray-900" title="Attach">
                            <Paperclip className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={worker.is_functional
                                ? `Send a task to ${worker.name}…`
                                : "This Workforce Member isn't activated yet."}
                            disabled={!worker.is_functional || streaming}
                            data-testid="composer-input"
                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400 disabled:cursor-not-allowed"
                        />
                        <button type="button" className="p-1.5 rounded-md text-gray-500 hover:text-gray-900" title="Emoji">
                            <Smile className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button type="button" className="p-1.5 rounded-md text-gray-500 hover:text-gray-900" title="Voice">
                            <Mic className="w-4 h-4" strokeWidth={1.75} />
                        </button>
                        <button
                            type="submit"
                            disabled={!worker.is_functional || !input.trim() || streaming}
                            data-testid="composer-send"
                            className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:bg-gray-200 disabled:text-gray-400"
                        >
                            {streaming ? "…" : <><Send className="w-3.5 h-3.5" strokeWidth={2} /> Send</>}
                        </button>
                    </div>
                </form>
            </main>

            {/* RIGHT RAIL */}
            <aside
                className="w-full xl:w-[340px] flex-shrink-0 border-t xl:border-t-0 xl:border-l border-[#e8e8e3] bg-[#fafaf7] flex flex-col xl:overflow-y-auto wfos-scrollbar p-6 gap-5"
                data-testid="worker-right-rail"
            >
                <div className="wfos-card p-4" data-testid="activity-status-counters">
                    <div className="flex items-center justify-between text-[11px] font-medium">
                        <span className="wfos-label">Last 24h</span>
                        <span className="text-gray-400">Activity</span>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-[13px]">
                        <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLOR.running}`} />{stats.running} Running</span>
                        <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLOR.paused}`} />{stats.paused} Paused</span>
                        <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLOR.done}`} />{stats.done} Done</span>
                        <span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLOR.failed}`} />{stats.failed} Failed</span>
                    </div>
                </div>

                <div>
                    <div className="wfos-label mb-2">Today</div>
                    <ul className="space-y-2" data-testid="activity-list">
                        {activity.length === 0 && (
                            <li className="text-sm text-gray-400">No activity yet.</li>
                        )}
                        {activity.map((a) => (
                            <li key={a.id} data-testid={`activity-row-${a.id}`} className="wfos-card p-3 flex items-start gap-3">
                                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_COLOR[a.status] || "bg-gray-300"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[13px] text-gray-900 truncate">{a.title}</div>
                                    <div className="text-[11px] text-gray-500">
                                        {fmtTime(a.timestamp)} · {fmtDate(a.timestamp)}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <TerminalBox workerName={worker.name} company={company} />
            </aside>

            {/* Modals */}
            {pickerOpen && catalog && (
                <IntegrationPickerModal
                    catalog={catalog}
                    recommendedIds={worker.recommended_integrations || []}
                    connectedIds={connectedIds}
                    onSelect={startOAuth}
                    onClose={closePicker}
                />
            )}
            {pendingOAuth && (
                <ConnectorOAuthModal
                    integration={pendingOAuth}
                    workerName={worker.name}
                    companyName={company?.name}
                    onCancel={cancelOAuth}
                    onAuthorize={confirmAuthorize}
                    busy={oauthBusy}
                />
            )}
        </div>
    );
}

/* ---------- Message row ---------- */
function MessageRow({ m, worker, streaming }) {
    const isUser = m.role === "user";
    return (
        <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`}>
            {!isUser && (
                <img
                    src={resolveAvatar(worker.avatar_url)}
                    alt={worker.name}
                    className="w-8 h-8 rounded-full object-cover border border-[#e8e8e3] flex-shrink-0"
                />
            )}
            <div className={`max-w-[70%] ${isUser ? "text-right" : ""}`}>
                <div className={`text-[11px] text-gray-400 mb-1 ${isUser ? "" : "pl-1"}`}>
                    {isUser ? "You" : worker.name} · {fmtTime(m.timestamp)}
                </div>
                <div
                    className={`inline-block rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap text-left ${
                        isUser
                            ? "bg-[#111827] text-white"
                            : "bg-[#fafaf7] border border-[#e8e8e3] text-gray-900"
                    } ${streaming ? "wfos-caret" : ""}`}
                >
                    {m.content || (streaming ? "…" : "")}
                </div>
                {!isUser && Array.isArray(m.artifacts) && m.artifacts.map((a, i) => (
                    <ArtifactCard key={i} artifact={a} />
                ))}
                {!isUser && !streaming && m.content && (
                    <div
                        className="mt-1 text-[10.5px] italic pl-1"
                        style={{ color: "var(--atlas-text-muted)" }}
                        data-testid="ai-message-footnote"
                    >
                        {m.ai_footnote || "AI-generated — review before external use."}
                    </div>
                )}
            </div>
        </div>
    );
}


/* ================================================================
   Sprint I-6 — Specialty disclaimer chip + modal (per Workforce Member)
   Fetches the current specialty body from /api/legal/disclaimers on demand.
   ================================================================ */
function SpecialtyDisclaimerBar({ workerSlug, disclaimerKey }) {
    const [open, setOpen] = useState(false);
    const [bank, setBank] = useState(null);

    useEffect(() => {
        // lazy fetch — one call per page load
        const cached = window.__ATLAS_DISCLAIMERS__;
        if (cached) { setBank(cached); return; }
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/legal/disclaimers`)
            .then((r) => {
                window.__ATLAS_DISCLAIMERS__ = r.data;
                setBank(r.data);
            })
            .catch(() => {});
    }, []);

    if (!bank) return null;
    const entry = bank.specialty_disclaimers?.[disclaimerKey] || bank.specialty_disclaimers?.generic;
    if (!entry) return null;

    return (
        <>
            <div
                data-testid={`specialty-disclaimer-bar-${workerSlug}`}
                className="border-b px-6 py-2 flex items-center gap-2"
                style={{
                    borderColor: "rgba(245,158,11,0.35)",
                    background: "rgba(245,158,11,0.08)",
                }}
            >
                <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} style={{ color: "#fbbf24" }} />
                <button
                    onClick={() => setOpen(true)}
                    data-testid="specialty-disclaimer-chip"
                    className="inline-flex items-center gap-1 rounded-full px-2.5 h-6 text-[10.5px] font-semibold uppercase tracking-widest"
                    style={{
                        background: "rgba(245,158,11,0.18)",
                        color: "#fbbf24",
                        border: "1px solid rgba(245,158,11,0.45)",
                    }}
                >
                    {entry.chip}
                </button>
                <span className="text-[11px]" style={{ color: "var(--atlas-text-muted)" }}>
                    Click for the full notice · AI-generated outputs require your review.
                </span>
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-[90] grid place-items-center px-4"
                    style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(8px)" }}
                    onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
                    data-testid="specialty-disclaimer-modal"
                >
                    <div className="atlas-card w-full max-w-[540px] p-6">
                        <div className="flex items-center gap-3">
                            <div
                                className="w-9 h-9 rounded-xl grid place-items-center"
                                style={{ background: "rgba(245,158,11,0.16)", border: "1px solid rgba(245,158,11,0.45)" }}
                            >
                                <AlertTriangle className="w-4 h-4" style={{ color: "#fbbf24" }} strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[16px] font-bold" style={{ color: "var(--atlas-text-primary)" }}>
                                    {entry.title}
                                </div>
                                <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "var(--atlas-text-muted)" }}>
                                    Specialty notice · {disclaimerKey}
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="specialty-disclaimer-close"
                                className="ml-auto atlas-btn-ghost rounded-md w-8 h-8 grid place-items-center"
                                aria-label="Close"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        </div>
                        <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: "var(--atlas-text-secondary)" }}>
                            {entry.body}
                        </p>
                        <div className="mt-5 text-right">
                            <Link
                                to="/legal/platform-notice"
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => setOpen(false)}
                                className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-3.5 h-9 text-[12.5px] font-medium mr-2"
                            >
                                Read full Platform Notice
                            </Link>
                            <button
                                onClick={() => setOpen(false)}
                                className="atlas-btn-primary rounded-lg px-4 h-9 text-[13px]"
                                data-testid="specialty-disclaimer-ok"
                            >
                                Understood
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
